import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import {
  supabase,
  signInWithDiscord,
  signOut,
  getSession,
  onAuthStateChange,
  unsubscribeAuth
} from '../utils/supabase'

const AuthContext = createContext(null)

const API_BASE = process.env.REACT_APP_API_BASE ||
  (window.location.hostname === 'localhost' ? '' : '/api');

// In-flight request deduplication cache
const inflightCache = new Map()
// Track tokens we've already loaded profile for (persists across re-renders)
const loadedTokens = new Set()

async function dedupedFetch(url, options, cacheKey) {
  // Check if there's an in-flight request with the same key
  if (inflightCache.has(cacheKey)) {
    return inflightCache.get(cacheKey)
  }

  const promise = fetch(url, options).finally(() => {
    // Clear from cache after request completes (success or error)
    inflightCache.delete(cacheKey)
  })

  inflightCache.set(cacheKey, promise)
  return promise
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [profileLoaded, setProfileLoaded] = useState(false)
  const [isBanned, setIsBanned] = useState(false)

  const logout = useCallback(async () => {
    setError(null)
    try {
      await signOut()
      setUser(null)
      setSession(null)
      setUserProfile(null)
      setIsBanned(false)
    } catch (err) {
      setError(err.message)
      throw err
    }
  }, [])

  // Track last successful/failed profile fetch per token to prevent rapid re-fetches
  const lastFetchRef = useRef({ token: null, time: 0 })
  // Synchronous guard to prevent race condition calls within same tick
  const fetchingRef = useRef(false)

  // Load user profile from backend - aggressively deduped to prevent rate limiting
  const loadUserProfile = useCallback(async (accessToken) => {
    try {
      if (!accessToken) return

      const tokenKey = accessToken.slice(-20)

      // Skip if we've already loaded this token globally (page session)
      if (loadedTokens.has(tokenKey)) {
        return
      }

      // Synchronous guard: if another call is already in progress this tick, bail
      if (fetchingRef.current) {
        return
      }

      // Prevent re-fetching within 5 seconds for the same token (React StrictMode, etc)
      const now = Date.now()
      if (lastFetchRef.current.token === accessToken && (now - lastFetchRef.current.time) < 5000) {
        return
      }

      fetchingRef.current = true
      lastFetchRef.current = { token: accessToken, time: now }

      const response = await dedupedFetch(`${API_BASE}/v1/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }, `profile:${tokenKey}`)

      // Mark as loaded regardless of response (we tried)
      loadedTokens.add(tokenKey)

      if (response.ok) {
        const data = await response.json()
        if (data.status === 'ok') {
          setUserProfile(data.data)
          setIsBanned(false)
        }
        setProfileLoaded(true)
      } else if (response.status === 403) {
        const data = await response.json().catch(() => ({}))
        if (data.error === 'banned') {
          setIsBanned(true)
          setProfileLoaded(true)
        }
      } else if (response.status === 401) {
        // Auth failed (e.g. JWKS not available) — don't mark profile as loaded
        console.warn('Profile auth failed:', response.status)
      } else {
        // Other errors (404, 500) — mark as loaded with no profile
        setProfileLoaded(true)
      }
    } catch (err) {
      console.error('Failed to load user profile:', err)
    } finally {
      fetchingRef.current = false
    }
  }, [])

  // Initialize auth state - only use onAuthStateChange for profile loading
  // to avoid duplicate requests (both initAuth and INITIAL_SESSION fire on load)
  useEffect(() => {
    let mounted = true

    async function initAuth() {
      try {
        const session = await getSession()
        if (mounted) {
          setSession(session)
          setUser(session?.user || null)
          // Note: onAuthStateChange will fire with INITIAL_SESSION and load profile
        }
      } catch (err) {
        if (mounted) {
          setError(err.message)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    initAuth()

    const { unsubscribe } = onAuthStateChange((event, session) => {
      if (!mounted) return

      setSession(session)
      setUser(session?.user || null)

      if (session?.access_token) {
        // Only load profile on sign-in events, not every auth state change
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          loadUserProfile(session.access_token)
        }
      } else {
        // User signed out - clear profile
        setUserProfile(null)
        setProfileLoaded(false)
        setIsBanned(false)
        // Clear loaded tokens so next login triggers a fresh fetch
        loadedTokens.clear()
      }
    })

    return () => {
      mounted = false
      unsubscribeAuth()
    }
  }, [loadUserProfile])

  const loginWithDiscord = useCallback(async () => {
    setError(null)
    try {
      await signInWithDiscord()
    } catch (err) {
      setError(err.message)
      throw err
    }
  }, [])

  const refreshProfile = useCallback(() => {
    const token = session?.access_token
    if (!token) return
    // Clear guards so explicit refresh actually fetches
    loadedTokens.delete(token.slice(-20))
    lastFetchRef.current = { token: null, time: 0 }
    fetchingRef.current = false
    return loadUserProfile(token)
  }, [session?.access_token, loadUserProfile])

  const refreshSession = useCallback(async () => {
    if (!supabase) return;
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error('Failed to refresh session:', error);
      return null;
    }
    setSession(data.session);
    setUser(data.session?.user || null);
    if (data.session?.access_token) {
      await loadUserProfile(data.session.access_token);
    }
    return data.session;
  }, [loadUserProfile])

  const value = {
    user,
    session,
    userProfile,
    profileLoaded,
    loading,
    error,
    isBanned,
    isAuthenticated: !!user,
    isMember: userProfile?.status === 'approved',
    isAdmin: userProfile?.role === 'admin' || userProfile?.role === 'owner',
    isOwner: userProfile?.role === 'owner',
    loginWithDiscord,
    logout,
    refreshProfile,
    refreshSession
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
